import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { KycDetails } from "@services/models/Kyc/GetKycP2PModel";
import { GetDisputedTradeListData } from "@services/models/Prime/GetDisputedTradeListModel";

// Define styles for PDF
const styles = StyleSheet.create({
  page: {
    position: "relative", // Ensure relative positioning
  },
  backgroundImage: {
    position: "absolute", // Place it behind all other content
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    zIndex: -1, // Ensure it is below all other components
  },
  contentWrapper: {
    padding: 20, // Add padding inside a wrapper to preserve content spacing
    flexGrow: 1, // Ensure it stretches to fill the page
  },
  header: {
    flexDirection: "row", // Arrange items in a row
    alignItems: "center", // Center items vertically
    justifyContent: "space-between", // Space between the logo and title
    marginBottom: 30,
  },
  logo: {
    width: 40,
    height: 16,
  },
  currencyImages: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  title: {
    fontSize: 14,
    marginLeft: 15,
    color: "#15182C",
    fontWeight: "semibold",
  },
  section: {
    marginBottom: 16,
    padding: 10,
    borderRadius: 6,
    backgroundColor: "#F7F7F7",
    borderWidth: 1, // Border thickness
    borderColor: "#ECECEC", // Border color
    borderStyle: "solid", // Optional: explicitly set the border style
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: "semibold",
    marginBottom: 12,
    color: "#15182C",
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  rowSpacing: {
    flexDirection: "row",
    alignItems: "center",
    rowGap: "8px",
  },
  col: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
  rowTextImages: {
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
  },
  currencyText: {
    fontSize: 10,
    marginRight: 4,
  },
  label: {
    fontSize: 10,
    fontWeight: "medium",
    color: "#686E92",
    flex: 1, // Allow label to take up space
    flexShrink: 0, // Prevent shrinking if text is too long
  },
  value: {
    fontSize: 10,
    textAlign: "right", // Align value to the right
    flex: 2, // Allow value to take up more space
    flexShrink: 0, // Prevent shrinking if text is too long
  },
  image: {
    width: 200,
    height: 120,
    marginVertical: 10,
  },
  divider: {
    height: 1,
    backgroundColor: "#ECECEC", // Divider color
  },
});

interface KYCPdfViewerProps {
  tradeData: GetDisputedTradeListData;
  viewerKycDetails: KycDetails;
  userId?: number;
}

const KYCPdfPrimeBuySellViewer: React.FC<KYCPdfViewerProps> = ({
  tradeData,
  viewerKycDetails,
  userId,
}) => {
  const kyc = viewerKycDetails;
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-indexed
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const maskEmail = (email: string): string => {
    const [localPart, domain] = email.split("@");
    if (localPart.length > 1) {
      return `${localPart[0]}****@${domain}`;
    }
    return `****@${domain}`;
  };

  const maskPhone = (phone: string): string => {
    if (phone.length > 4) {
      const visibleDigits = phone.slice(-4);
      return `******${visibleDigits}`;
    }
    return "****";
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Background Image */}

        <View style={styles.contentWrapper}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>Prime Trade & KYC Summary</Text>
          </View>

          {/* KYC section*/}
          <View style={styles.section}>
            {/* Container for Personal and Address Details */}
            <View
              style={{ flexDirection: "row", justifyContent: "space-between" }}
            >
              {/* Personal Details Section */}
              <View style={{ flex: 1, marginRight: 10 }}>
                <Text style={[styles.sectionTitle, { marginBottom: 8 }]}>
                  Personal:
                </Text>
                <View style={styles.row}>
                  <Text style={styles.label}>First name</Text>
                  <Text style={styles.value}>
                    {kyc.userPersonalInfo.firstName}
                  </Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Last name</Text>
                  <Text style={styles.value}>
                    {kyc.userPersonalInfo.lastName}
                  </Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Email ID</Text>
                  <Text style={styles.value}>
                    {maskEmail(kyc.userPersonalInfo.email)}
                  </Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Number</Text>
                  <Text style={styles.value}>
                    {maskPhone(kyc.userPersonalInfo.phone)}
                  </Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>DOB</Text>
                  <Text style={styles.value}>
                    {formatDate(kyc.userPersonalInfo.dateOfBirth)}
                  </Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Country</Text>
                  <Text style={styles.value}>
                    {kyc.governmentIdInfo.govIdCountryName ?? ""}
                  </Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>ID Type</Text>
                  <Text style={styles.value}>
                    {kyc.governmentIdInfo.govIdType ?? "N/A"}
                  </Text>
                </View>
                <View style={[styles.col, { marginBottom: 0 }]}>
                  {(kyc.governmentIdInfo.govIdFrontUrl ||
                    kyc.governmentIdInfo.govIdBackUrl) && (
                    <Text style={[styles.label, { marginBottom: 8 }]}>
                      ID Image
                    </Text>
                  )}
                  <View style={styles.rowSpacing}>
                    {kyc.governmentIdInfo.govIdFrontUrl && (
                      <Image
                        src={kyc.governmentIdInfo.govIdFrontUrl}
                        style={styles.image}
                      />
                    )}
                    {kyc.governmentIdInfo.govIdBackUrl && (
                      <Image
                        src={kyc.governmentIdInfo.govIdBackUrl}
                        style={styles.image}
                      />
                    )}
                  </View>
                </View>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default KYCPdfPrimeBuySellViewer;
